"use client";

import { c as _c } from "react/compiler-runtime";
import { cn } from "@/utils/cn";
import { getLocalTimeZone, today } from "@internationalized/date";
import { ChevronLeft, ChevronRight } from "lucide-react";
import * as React from "react";
import { Button as AriaButton, Calendar as AriaCalendar, CalendarCell as AriaCalendarCell, CalendarCellProps as AriaCalendarCellProps, CalendarGrid as AriaCalendarGrid, CalendarGridBody as AriaCalendarGridBody, CalendarGridBodyProps as AriaCalendarGridBodyProps, CalendarGridHeader as AriaCalendarGridHeader, CalendarGridHeaderProps as AriaCalendarGridHeaderProps, CalendarGridProps as AriaCalendarGridProps, CalendarHeaderCell as AriaCalendarHeaderCell, CalendarHeaderCellProps as AriaCalendarHeaderCellProps, CalendarProps as AriaCalendarProps, Heading as AriaHeading, RangeCalendar as AriaRangeCalendar, RangeCalendarStateContext as AriaRangeCalendarStateContext, CalendarStateContext, composeRenderProps, DateValue, useLocale } from "react-aria-components";
function useCalendarStateContext() {
  const state = React.useContext(CalendarStateContext);
  if (!state) {
    throw new Error("Calendar components must be used inside <Calendar />");
  }
  return state;
}
const Calendar = AriaCalendar;
const RangeCalendar = AriaRangeCalendar;
const CalendarHeading = t0 => {
  const $ = _c(24);
  const {
    mode
  } = t0;
  const {
    direction
  } = useLocale();
  const state = useCalendarStateContext();
  let title;
  const isShowRTL = mode !== "month";
  if (mode === "day") {
    let t1;
    if ($[0] !== direction) {
      t1 = <AriaButton slot="previous" className="size-8 p-0">{direction === "rtl" ? <ChevronRight /> : <ChevronLeft />}</AriaButton>;
      $[0] = direction;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <AriaHeading className="text-base font-medium" />;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    let t3;
    if ($[3] !== direction) {
      t3 = <AriaButton slot="next" className="size-8 p-0">{direction === "rtl" ? <ChevronLeft /> : <ChevronRight />}</AriaButton>;
      $[3] = direction;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    let t4;
    if ($[5] !== t1 || $[6] !== t3) {
      t4 = <header className="flex items-center justify-between px-6 py-4">{t1}{t2}{t3}</header>;
      $[5] = t1;
      $[6] = t3;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    return t4;
  }
  if (mode === "month") {
    title = "Jan - Dec";
  } else {
    const startYear = Math.floor(state.focusedDate.year / 12) * 12;
    title = `${startYear} – ${startYear + 11}`;
  }
  let t1;
  if ($[8] !== direction || $[9] !== isShowRTL || $[10] !== mode || $[11] !== state) {
    t1 = isShowRTL && <button className="mr-2 cursor-pointer" onClick={() => state.setFocusedDate(state.focusedDate.subtract(mode === "year" ? {
      years: 12
    } : {
      years: 1
    }))}>{direction === "rtl" ? <ChevronRight /> : <ChevronLeft />}</button>;
    $[8] = direction;
    $[9] = isShowRTL;
    $[10] = mode;
    $[11] = state;
    $[12] = t1;
  } else {
    t1 = $[12];
  }
  let t2;
  if ($[13] !== title) {
    t2 = <span className="text-base font-medium">{title}</span>;
    $[13] = title;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  let t3;
  if ($[15] !== direction || $[16] !== isShowRTL || $[17] !== mode || $[18] !== state) {
    t3 = isShowRTL && <button className="ml-2 cursor-pointer" onClick={() => state.setFocusedDate(state.focusedDate.add(mode === "year" ? {
      years: 12
    } : {
      years: 1
    }))}>{direction === "rtl" ? <ChevronLeft /> : <ChevronRight />}</button>;
    $[15] = direction;
    $[16] = isShowRTL;
    $[17] = mode;
    $[18] = state;
    $[19] = t3;
  } else {
    t3 = $[19];
  }
  let t4;
  if ($[20] !== t1 || $[21] !== t2 || $[22] !== t3) {
    t4 = <header className="flex items-center justify-between px-6 py-1">{t1}{t2}{t3}</header>;
    $[20] = t1;
    $[21] = t2;
    $[22] = t3;
    $[23] = t4;
  } else {
    t4 = $[23];
  }
  return t4;
};
const CalendarGrid = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("w-full border-collapse", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <AriaCalendarGrid className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
const CalendarGridHeader = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("[&>tr]:flex", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <AriaCalendarGridHeader className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
const CalendarHeaderCell = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("w-10 h-10 flex items-center justify-center text-sm font-medium text-neutral-600", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <AriaCalendarHeaderCell className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
const CalendarGridBody = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("[&>tr]:flex [&>tr>td]:p-0", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <AriaCalendarGridBody className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
const CalendarCell = t0 => {
  const $ = _c(11);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  const isRange = Boolean(React.useContext(AriaRangeCalendarStateContext));
  let t1;
  if ($[3] !== isRange) {
    t1 = (className_0, renderProps) => cn("relative flex size-10 items-center justify-center p-0 text-sm font-medium rounded-lg transition-colors cursor-pointer", "text-neutral-700 hover:bg-accent", renderProps.isDisabled && "text-neutral-400 cursor-not-allowed hover:bg-transparent", renderProps.isSelected && "bg-primary text-primary-foreground hover:bg-primary data-[focused]:bg-primary data-[focused]:text-primary-foreground", renderProps.isSelected && isRange && !renderProps.isSelectionStart && !renderProps.isSelectionEnd && "rounded-none bg-primary/10 text-primary hover:bg-primary/20", renderProps.isOutsideMonth && "text-neutral-400 hover:bg-transparent data-[selected]:bg-primary/50 data-[selected]:text-primary-foreground", renderProps.date.compare(today(getLocalTimeZone()) as any) === 0 && !renderProps.isSelected && "bg-accent text-accent-foreground font-semibold", renderProps.isUnavailable && "cursor-not-allowed text-destructive opacity-50", renderProps.isInvalid && "bg-destructive text-destructive-foreground data-[focused]:bg-destructive data-[hovered]:bg-destructive", className_0);
    $[3] = isRange;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== className || $[6] !== t1) {
    t2 = composeRenderProps(className, t1);
    $[5] = className;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== props || $[9] !== t2) {
    t3 = <AriaCalendarCell className={t2} {...props} />;
    $[8] = props;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
};
export type CalendarMode = "day" | "month" | "year";
const DayCalendarBody = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <CalendarGrid><CalendarGridHeader>{_temp}</CalendarGridHeader><CalendarGridBody>{_temp2}</CalendarGridBody></CalendarGrid>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const MonthCalendarBody = () => {
  const $ = _c(3);
  const state = useCalendarStateContext();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = Array.from({
      length: 12
    }, _temp3);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const months = t0;
  let t1;
  if ($[1] !== state) {
    t1 = <div className="grid grid-cols-3 gap-2 p-2">{months.map(month => {
        const isSelected = state.focusedDate.month === month;
        return <button key={month} className={cn("p-1 rounded-lg text-sm font-medium cursor-pointer", isSelected ? "bg-primary text-primary-foreground" : "hover:bg-accent")} onClick={() => {
          const newDate = state.focusedDate.set({
            month
          });
          state.setFocusedDate(newDate);
          state.selectDate(newDate);
        }}>{new Intl.DateTimeFormat(undefined, {
            month: "short"
          }).format(new Date(2024, month - 1))}</button>;
      })}</div>;
    $[1] = state;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
const YearCalendarBody = () => {
  const $ = _c(5);
  const state = useCalendarStateContext();
  const startYear = Math.floor(state.focusedDate.year / 12) * 12;
  let t0;
  if ($[0] !== startYear) {
    t0 = Array.from({
      length: 12
    }, (_, i) => startYear + i);
    $[0] = startYear;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const years = t0;
  let t1;
  if ($[2] !== state || $[3] !== years) {
    t1 = <div className="grid grid-cols-3 gap-2 p-4">{years.map(year => {
        const isSelected = state.focusedDate.year === year;
        return <button key={year} className={cn("p-1 rounded-lg text-sm font-medium cursor-pointer", isSelected ? "bg-primary text-primary-foreground" : "hover:bg-accent")} onClick={() => {
          const newDate = state.focusedDate.set({
            year
          });
          state.setFocusedDate(newDate);
          state.selectDate(newDate);
        }}>{year}</button>;
      })}</div>;
    $[2] = state;
    $[3] = years;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};
function JollyCalendar(t0) {
  const $ = _c(16);
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      mode: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = t1;
  } else {
    props = $[1];
    t1 = $[2];
  }
  const mode = t1 === undefined ? "day" : t1;
  let t2;
  let t3;
  if ($[3] !== mode) {
    t2 = <CalendarHeading mode={mode} />;
    t3 = mode === "day" && <DayCalendarBody />;
    $[3] = mode;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  let t4;
  if ($[6] !== mode) {
    t4 = mode === "month" && <MonthCalendarBody />;
    $[6] = mode;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== mode) {
    t5 = mode === "year" && <YearCalendarBody />;
    $[8] = mode;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== props || $[11] !== t2 || $[12] !== t3 || $[13] !== t4 || $[14] !== t5) {
    t6 = <Calendar {...props}>{t2}{t3}{t4}{t5}</Calendar>;
    $[10] = props;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
}
export { Calendar, CalendarCell, CalendarGrid, CalendarGridBody, CalendarGridHeader, CalendarHeaderCell, CalendarHeading, JollyCalendar, RangeCalendar };
function _temp(day) {
  return <CalendarHeaderCell>{day}</CalendarHeaderCell>;
}
function _temp2(date) {
  return <CalendarCell date={date} />;
}
function _temp3(_, i) {
  return i + 1;
}